import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import MapView from '@arcgis/core/views/MapView';
import { useEffect, useRef, useState } from 'react';
import { onViewStationary } from '../jsapi/js-api-util';
import styles from './MapTip.module.css';

function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

// Adds a map tip to a view.
const MapTip = ({
    view,
    layer,
    tip
}: {
    view: MapView | undefined;
    layer: FeatureLayer;
    tip: Function;
}) => {
    const tipRef: any = useRef();
    const [text, setText] = useState<string>('');
    const [pointerEvent, setPointerEvent] = useState<any>();
    const [tipActive, setTipActive] = useState<boolean>(true);
    const [left, setLeft] = useState<string>('0px');
    const [top, setTop] = useState<string>('0px');
    const [display, setDisplay] = useState<string>('none');
    const style = { left, top, display };
    const debouncedEvent: any = useDebounce<string>(pointerEvent, 5);

    // Initialise when view ready.
    useEffect(() => {
        if (view !== undefined) {
            // Check view stationary state.
            onViewStationary({
                view,
                onChange: (isStationary: boolean) => setTipActive(isStationary)
            });
            // Listen for view pointer events.
            // Use as "any" to avoid linting error.
            // Argument of type 'string[]' is not assignable to parameter of type '"blur"'.
            view.on(
                [
                    'pointer-move',
                    'pointer-down',
                    'pointer-up',
                    'pointer-leave',
                    'pointer-enter'
                ] as any,
                (event: any) => {
                    if (
                        event.type === 'pointer-down' ||
                        event.type === 'pointer-leave'
                    )
                        setTipActive(false);
                    else if (
                        event.type === 'pointer-up' ||
                        event.type === 'pointer-enter'
                    )
                        setTipActive(true);
                    else setPointerEvent(event);
                }
            );
        }
    }, [view]);

    useEffect(() => {
        if (pointerEvent !== undefined && tipActive === true) {
            const bounds = tipRef.current.getBoundingClientRect();
            setLeft(`${pointerEvent.x - bounds.width / 2}px`);
            setTop(`${pointerEvent.y - bounds.height - 10}px`);
        }
    }, [pointerEvent, tipActive]);

    useEffect(() => {
        const onPointerMoved = async () => {
            if (
                view !== undefined &&
                layer !== undefined &&
                tip !== undefined &&
                debouncedEvent?.type === 'pointer-move' &&
                tipActive === true
            ) {
                const response = await view.hitTest(debouncedEvent);
                const results: any = response.results.filter(
                    (f: any) => f.graphic?.layer?.id === layer.id
                );
                if (results.length > 0) {
                    setText(tip(results[0].graphic));
                    setDisplay('block');
                } else {
                    setText('');
                    setDisplay('none');
                }
            } else {
                setText('');
                setDisplay('none');
            }
        };
        onPointerMoved();
    }, [layer, debouncedEvent, tip, tipActive, view]);

    return (
        <div ref={tipRef} className={styles.mapTip} style={style}>
            {text}
        </div>
    );
};

export default MapTip;
